import jsPDF from 'jspdf';

// ATS-Friendly Theme Configuration
const ATS_FRIENDLY_THEME = {
  colors: {
    // Use black text only for maximum ATS compatibility
    text: '#000000',
    background: '#ffffff'
  },
  fonts: {
    // Use standard fonts that are universally recognized by ATS
    primary: 'Times',
    secondary: 'Times'
  },
  spacing: {
    margin: 20,
    section: 12,
    line: 5,
    paragraph: 3
  },
  fontSize: {
    name: 16,
    title: 14,
    sectionHeader: 12,
    body: 10,
    contact: 10
  }
};

class ATSPdfGenerator {
  constructor() {
    this.doc = null;
    this.currentY = 0;
    this.theme = ATS_FRIENDLY_THEME;
    this.pageWidth = 210; // A4 width in mm
    this.pageHeight = 297; // A4 height in mm
    this.margin = this.theme.spacing.margin;
    this.contentWidth = this.pageWidth - (this.margin * 2);
  }

  // Initialize PDF document
  init() {
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.currentY = this.margin;

    // Set default font - Times for maximum ATS compatibility
    this.doc.setFont(this.theme.fonts.primary, 'normal');
    this.doc.setFontSize(this.theme.fontSize.body);
    this.doc.setTextColor(0, 0, 0); // Pure black text
  }

  // Add header section with ATS-friendly format
  addHeader(name, title, email, phone, location) {
    // Name - Large, bold, left-aligned (ATS prefers left alignment)
    this.doc.setFont(this.theme.fonts.primary, 'bold');
    this.doc.setFontSize(this.theme.fontSize.name);
    this.doc.text(name, this.margin, this.currentY);
    this.currentY += 8;

    // Title - Medium, left-aligned
    this.doc.setFont(this.theme.fonts.primary, 'normal');
    this.doc.setFontSize(this.theme.fontSize.title);
    this.doc.text(title, this.margin, this.currentY);
    this.currentY += 6;

    // Contact info - Each on separate line for better ATS parsing
    this.doc.setFontSize(this.theme.fontSize.contact);

    if (email) {
      this.doc.text(`Email: ${email}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    if (phone) {
      this.doc.text(`Phone: ${phone}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    if (location) {
      this.doc.text(`Location: ${location}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    this.currentY += 8; // Extra space after header
  }

  // Add section title with ATS-friendly formatting
  addSectionTitle(title) {
    this.checkPageBreak(15);

    this.doc.setFont(this.theme.fonts.primary, 'bold');
    this.doc.setFontSize(this.theme.fontSize.sectionHeader);
    this.doc.text(title.toUpperCase(), this.margin, this.currentY);
    this.currentY += 8;
  }

  // Add professional summary
  addSummary(summary) {
    if (!summary) return;

    this.addSectionTitle('Professional Summary');

    this.doc.setFont(this.theme.fonts.primary, 'normal');
    this.doc.setFontSize(this.theme.fontSize.body);

    const summaryLines = this.doc.splitTextToSize(summary, this.contentWidth);
    summaryLines.forEach(line => {
      this.checkPageBreak(6);
      this.doc.text(line, this.margin, this.currentY);
      this.currentY += this.theme.spacing.line;
    });

    this.currentY += this.theme.spacing.section;
  }

  // Add work experience entry with ATS-friendly format
  addWorkExperience(position, company, period, location, summary, achievements) {
    this.checkPageBreak(30);

    // Position - Bold
    this.doc.setFont(this.theme.fonts.primary, 'bold');
    this.doc.setFontSize(this.theme.fontSize.body);
    this.doc.text(position, this.margin, this.currentY);
    this.currentY += 6;

    // Company and Period - Regular text, separate lines for better parsing
    this.doc.setFont(this.theme.fonts.primary, 'normal');

    if (company) {
      this.doc.text(`Company: ${company}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    if (period) {
      this.doc.text(`Period: ${period}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    if (location) {
      this.doc.text(`Location: ${location}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    this.currentY += 3;

    // Summary if provided
    if (summary) {
      const summaryLines = this.doc.splitTextToSize(summary, this.contentWidth - 5);
      summaryLines.forEach(line => {
        this.checkPageBreak(6);
        this.doc.text(line, this.margin, this.currentY);
        this.currentY += this.theme.spacing.line;
      });
      this.currentY += 3;
    }

    // Achievements - Use simple dashes instead of bullets for ATS compatibility
    if (achievements && achievements.length > 0) {
      achievements.forEach(achievement => {
        const lines = this.doc.splitTextToSize(`- ${achievement}`, this.contentWidth - 5);
        lines.forEach(line => {
          this.checkPageBreak(6);
          this.doc.text(line, this.margin + 3, this.currentY);
          this.currentY += this.theme.spacing.line;
        });
        this.currentY += 2;
      });
    }

    this.currentY += this.theme.spacing.section;
  }

  // Add education entry
  addEducation(degree, institution, period, location, studyType) {
    this.checkPageBreak(20);

    // Degree - Bold
    this.doc.setFont(this.theme.fonts.primary, 'bold');
    this.doc.setFontSize(this.theme.fontSize.body);
    this.doc.text(degree, this.margin, this.currentY);
    this.currentY += 6;

    // Institution details - Regular text, separate lines
    this.doc.setFont(this.theme.fonts.primary, 'normal');

    if (institution) {
      this.doc.text(`Institution: ${institution}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    if (period) {
      this.doc.text(`Period: ${period}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    if (location) {
      this.doc.text(`Location: ${location}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    if (studyType && studyType !== degree) {
      this.doc.text(`Study Type: ${studyType}`, this.margin, this.currentY);
      this.currentY += 5;
    }

    this.currentY += this.theme.spacing.section;
  }

  // Add skills section with ATS-friendly single column format
  addSkills(skillsData) {
    if (!skillsData || skillsData.length === 0) return;

    this.addSectionTitle('Technical Skills');

    // Group skills by category
    const skillsByCategory = {};
    skillsData.forEach(skill => {
      const category = skill.category && skill.category[0] ? skill.category[0] : 'Technical Skills';
      if (!skillsByCategory[category]) {
        skillsByCategory[category] = [];
      }
      skillsByCategory[category].push(skill.title);
    });

    // Display each category with skills in a simple, ATS-readable format
    Object.entries(skillsByCategory).forEach(([category, skills]) => {
      this.checkPageBreak(15);

      // Category name
      this.doc.setFont(this.theme.fonts.primary, 'bold');
      this.doc.setFontSize(this.theme.fontSize.body);
      this.doc.text(`${category}:`, this.margin, this.currentY);
      this.currentY += 6;

      // Skills as comma-separated list for better ATS parsing
      this.doc.setFont(this.theme.fonts.primary, 'normal');
      const skillsList = skills.join(', ');
      const skillLines = this.doc.splitTextToSize(skillsList, this.contentWidth - 5);

      skillLines.forEach(line => {
        this.checkPageBreak(6);
        this.doc.text(line, this.margin + 3, this.currentY);
        this.currentY += this.theme.spacing.line;
      });

      this.currentY += 6;
    });

    this.currentY += this.theme.spacing.section;
  }

  // Add certifications with ATS-friendly format
  addCertifications(certifications) {
    if (!certifications || certifications.length === 0) return;

    this.addSectionTitle('Certifications');

    certifications.forEach(cert => {
      this.checkPageBreak(15);

      // Certification title - Bold
      this.doc.setFont(this.theme.fonts.primary, 'bold');
      this.doc.setFontSize(this.theme.fontSize.body);
      this.doc.text(cert.title, this.margin, this.currentY);
      this.currentY += 6;

      // Issuer and ID - Regular text, separate lines
      this.doc.setFont(this.theme.fonts.primary, 'normal');

      if (cert.issuer) {
        this.doc.text(`Issuer: ${cert.issuer}`, this.margin, this.currentY);
        this.currentY += 5;
      }

      if (cert.id) {
        this.doc.text(`Credential ID: ${cert.id}`, this.margin, this.currentY);
        this.currentY += 5;
      }

      if (cert.date) {
        this.doc.text(`Date: ${cert.date}`, this.margin, this.currentY);
        this.currentY += 5;
      }

      this.currentY += 8;
    });
  }

  // Add projects section (optional, but ATS-friendly if included)
  addProjects(projects) {
    if (!projects || projects.length === 0) return;

    this.addSectionTitle('Key Projects');

    projects.forEach(project => {
      this.checkPageBreak(20);

      // Project name - Bold
      this.doc.setFont(this.theme.fonts.primary, 'bold');
      this.doc.setFontSize(this.theme.fontSize.body);
      this.doc.text(project.name, this.margin, this.currentY);
      this.currentY += 6;

      // Project description
      if (project.description) {
        this.doc.setFont(this.theme.fonts.primary, 'normal');
        const descLines = this.doc.splitTextToSize(project.description, this.contentWidth - 5);
        descLines.forEach(line => {
          this.checkPageBreak(6);
          this.doc.text(line, this.margin, this.currentY);
          this.currentY += this.theme.spacing.line;
        });
        this.currentY += 3;
      }

      // Technologies used
      if (project.stack && project.stack.length > 0) {
        this.doc.text(`Technologies: ${project.stack.join(', ')}`, this.margin, this.currentY);
        this.currentY += 5;
      }

      // Project links
      if (project.sourceCode) {
        this.doc.text(`Source Code: ${project.sourceCode}`, this.margin, this.currentY);
        this.currentY += 5;
      }

      if (project.livePreview) {
        this.doc.text(`Live Demo: ${project.livePreview}`, this.margin, this.currentY);
        this.currentY += 5;
      }

      this.currentY += 8;
    });
  }

  // Check if we need a new page
  checkPageBreak(requiredSpace = 15) {
    if (this.currentY + requiredSpace > this.pageHeight - this.margin) {
      this.doc.addPage();
      this.currentY = this.margin;
      return true;
    }
    return false;
  }

  // Generate complete ATS-friendly resume PDF
  generateResume(resumeData) {
    try {
      this.init();

      // Header
      this.addHeader(
        resumeData.name,
        resumeData.title,
        resumeData.email,
        resumeData.phone,
        resumeData.location
      );

      // Professional Summary
      if (resumeData.summary) {
        this.addSummary(resumeData.summary);
      }

      // Professional Experience
      if (resumeData.experience && resumeData.experience.length > 0) {
        this.addSectionTitle('Professional Experience');

        resumeData.experience.forEach(exp => {
          this.addWorkExperience(
            exp.position,
            exp.company,
            exp.period,
            exp.location,
            exp.summary,
            exp.achievements || []
          );
        });
      }

      // Education
      if (resumeData.education && resumeData.education.length > 0) {
        this.addSectionTitle('Education');

        resumeData.education.forEach(edu => {
          this.addEducation(
            edu.degree,
            edu.institution,
            edu.period,
            edu.location,
            edu.studyType
          );
        });
      }

      // Technical Skills
      if (resumeData.skills && resumeData.skills.length > 0) {
        this.addSkills(resumeData.skills);
      }

      // Certifications
      if (resumeData.certifications && resumeData.certifications.length > 0) {
        this.addCertifications(resumeData.certifications);
      }

      // Projects (optional - only include if resume is not too long)
      if (resumeData.projects && resumeData.projects.length > 0 && resumeData.includeProjects !== false) {
        this.addProjects(resumeData.projects.slice(0, 3)); // Limit to 3 most important projects
      }

      return this.doc;

    } catch (error) {
      console.error('Error generating ATS-friendly PDF:', error);
      throw new Error('Failed to generate ATS-friendly PDF resume');
    }
  }

  // Save the PDF
  save(filename = 'resume_ats_friendly.pdf') {
    if (!this.doc) {
      throw new Error('PDF not generated yet');
    }
    this.doc.save(filename);
  }
}

// Export function for easy use
export const generateATSFriendlyResume = (resumeData) => {
  const generator = new ATSPdfGenerator();
  const doc = generator.generateResume(resumeData);
  return doc;
};

export default ATSPdfGenerator;
